// Copyright (c) 2026 Watch Tower Bible and Tract Society of Pennsylvania
// All Rights Reserved. Proprietary and confidential.
// Unauthorized copying, modification, or distribution is strictly prohibited.
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Upload, BookOpen, Wrench, FolderUp, FileUp } from 'lucide-react';
import FolderSelector from './upload/FolderSelector';
import SelectedFilesList from './upload/SelectedFilesList';
import { useFileDropzone } from '../hooks/useFileDropzone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function loadAllFolders(category, parentId = null, prefix = '') {
  try {
    const params = new URLSearchParams();
    params.append('category', category);
    if (parentId) params.append('parent_id', parentId);
    const response = await axios.get(`${API}/folders?${params.toString()}`);
    let all = [];
    for (const folder of response.data) {
      all.push({ ...folder, displayName: prefix + folder.name });
      const sub = await loadAllFolders(category, folder.id, prefix + folder.name + ' / ');
      all = [...all, ...sub];
    }
    return all;
  } catch (error) {
    console.error('Error loading folders:', error);
    return [];
  }
}

export default function UploadModal({
  open,
  onOpenChange,
  onSuccess,
  defaultCategory,
  currentFolderId,
}) {
  const [category] = useState(defaultCategory || 'congregacao');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMode, setUploadMode] = useState('files');
  const [replaceExisting, setReplaceExisting] = useState(false);

  // Folder selection
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(currentFolderId || 'root');
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  const isCongrengacao = category === 'congregacao';
  const AreaIcon = isCongrengacao ? BookOpen : Wrench;
  const areaColor = isCongrengacao ? 'text-blue-700' : 'text-green-700';
  const areaBgColor = isCongrengacao ? 'bg-blue-50' : 'bg-green-50';
  const areaBorderColor = isCongrengacao ? 'border-blue-200' : 'border-green-200';
  const areaName = isCongrengacao ? 'Contas da Congregação' : 'Contas da Manutenção';

  const { dragActive, handleDrag, handleDrop } = useFileDropzone((newFiles, mode) => {
    setFiles(newFiles);
    setUploadMode(mode);
  });

  useEffect(() => {
    if (open) {
      loadAllFolders(category).then(setFolders);
      setSelectedFolderId(currentFolderId || 'root');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, category]);

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Digite um nome para a pasta');
      return;
    }
    setCreatingFolder(true);
    try {
      const parentId = selectedFolderId === 'root' ? null : selectedFolderId;
      const response = await axios.post(`${API}/folders`, {
        name: newFolderName.trim(),
        category,
        parent_id: parentId,
      });
      toast.success('Pasta criada!');
      setNewFolderName('');
      setShowNewFolder(false);
      const refreshed = await loadAllFolders(category);
      setFolders(refreshed);
      setSelectedFolderId(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar pasta');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(Array.from(e.target.files).map(f => ({ file: f, relativePath: f.name })));
      setUploadMode('files');
    }
  };

  const handleFolderSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(Array.from(e.target.files).map(f => ({
        file: f,
        relativePath: f.webkitRelativePath || f.name,
      })));
      setUploadMode('folder');
    }
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Selecione pelo menos um arquivo');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      const relativePaths = [];
      files.forEach((item) => {
        formData.append('files', item.file);
        relativePaths.push(item.relativePath);
      });
      formData.append('category', category);
      formData.append('relative_paths', JSON.stringify(relativePaths));
      formData.append('replace_existing', replaceExisting ? 'true' : 'false');

      const targetFolderId = selectedFolderId === 'root' ? '' : selectedFolderId;
      if (targetFolderId) formData.append('folder_id', targetFolderId);

      await axios.post(`${API}/files/upload-batch`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      toast.success(`${files.length} arquivo(s) enviado(s) com sucesso!`);
      setFiles([]);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Erro ao enviar arquivo(s)');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const resetModal = () => {
    setFiles([]);
    setUploadProgress(0);
    setUploadMode('files');
    setShowNewFolder(false);
    setNewFolderName('');
    setReplaceExisting(false);
    setSelectedFolderId(currentFolderId || 'root');
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) resetModal();
        onOpenChange(newOpen);
      }}
    >
      <DialogContent
        className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="upload-modal"
      >
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Enviar Arquivos</DialogTitle>
          <DialogDescription>
            Selecione a pasta de destino e os arquivos para enviar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1">
          {/* Area indicator */}
          <div className={`flex items-center gap-3 p-3 rounded-lg ${areaBgColor} border ${areaBorderColor}`}>
            <AreaIcon className={`w-5 h-5 ${areaColor}`} />
            <div>
              <p className={`font-medium text-sm ${areaColor}`}>{areaName}</p>
            </div>
          </div>

          <FolderSelector
            folders={folders}
            selectedFolderId={selectedFolderId}
            setSelectedFolderId={setSelectedFolderId}
            showNewFolder={showNewFolder}
            setShowNewFolder={setShowNewFolder}
            newFolderName={newFolderName}
            setNewFolderName={setNewFolderName}
            creatingFolder={creatingFolder}
            onCreateFolder={handleCreateFolder}
          />

          {/* Upload type tabs */}
          <div className="flex gap-2">
            <Button
              type="button"
              variant={uploadMode === 'files' ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => fileInputRef.current?.click()}
              data-testid="select-files-button"
            >
              <FileUp className="w-4 h-4 mr-2" />
              Arquivos
            </Button>
            <Button
              type="button"
              variant={uploadMode === 'folder' ? 'default' : 'outline'}
              className="flex-1"
              onClick={() => folderInputRef.current?.click()}
              data-testid="select-folder-button"
            >
              <FolderUp className="w-4 h-4 mr-2" />
              Pasta
            </Button>
          </div>

          {/* Hidden inputs */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            data-testid="file-input"
          />
          <input
            ref={folderInputRef}
            type="file"
            webkitdirectory=""
            directory=""
            multiple
            onChange={handleFolderSelect}
            className="hidden"
            data-testid="folder-input"
          />

          {/* Dropzone */}
          <div
            className={`upload-dropzone p-6 text-center cursor-pointer ${dragActive ? 'dragging' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() =>
              uploadMode === 'folder' ? folderInputRef.current?.click() : fileInputRef.current?.click()
            }
            data-testid="upload-dropzone"
          >
            <FolderUp className="w-10 h-10 mx-auto text-slate-400 mb-3" />
            <p className="text-sm text-slate-600 mb-1">Arraste arquivos ou pastas aqui</p>
            <p className="text-xs text-slate-400">A estrutura de pastas será mantida</p>
          </div>

          {/* Replace existing */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="replace-existing"
              checked={replaceExisting}
              onCheckedChange={setReplaceExisting}
              data-testid="replace-existing-checkbox"
            />
            <Label htmlFor="replace-existing" className="text-sm text-slate-600 cursor-pointer">
              Substituir arquivos existentes com mesmo nome
            </Label>
          </div>

          <SelectedFilesList files={files} uploadMode={uploadMode} onRemove={removeFile} />

          {uploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Enviando...</span>
                <span className="text-slate-900 font-medium">{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={uploading}
            data-testid="cancel-upload-button"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="confirm-upload-button"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Enviando...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Enviar {files.length > 0 && `(${files.length})`}
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
